import { CommonModule, HashLocationStrategy, LocationStrategy, LOCATION_INITIALIZED } from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, Injectable, Injector, LOCALE_ID, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Routes } from '@angular/router';
import { NliMenuModule } from '@next-level-integration/menu';
import { TranslateModule } from '@ngx-translate/core';
import { AuthModule, OidcSecurityService } from 'angular-auth-oidc-client';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { ClientComponent } from './containers/nli-client/nli-client.component';
import { LoginComponent } from './containers/nli-login/nli-login.component';
import { NliTitleBarComponent } from './containers/nli-title-bar/nli-title-bar.component';
import { NliToolbarComponent } from './containers/nli-toolbar/nli-toolbar.component';
import { AuthenticationService } from './core/authentication/auth.service';
import { SetupGuard } from './core/authentication/setup.guard';
import { JwtInterceptor } from './core/interceptor/jwt.interceptor';
import { ColorsGrabberComponent } from './helpers/colors-grabber.component';
import { KeycloakService } from './keycloak/keycloak.service';
import { MaterialModule } from './material.module';
import { FOR_ROOT_OPTIONS_TOKEN, ModuleOptionsModel } from './models/moduleOptions.model';
import { ClientService } from './services/client.service';
import { ModuleConfigurationService } from './services/configuration.service';
import { FilterSearchStateService } from './services/filter-search-state.service';
import { TitleConfigService } from './services/title-bar-configuration.service';

const NliComponents = [ClientComponent, LoginComponent, NliTitleBarComponent, NliToolbarComponent];

export const commonRoutes: Routes = [
  { path: 'clients', component: ClientComponent, canActivate: [SetupGuard] },
  { path: 'loginpage', component: LoginComponent, canActivate: [SetupGuard] },
  { path: '', redirectTo: 'clients', pathMatch: 'full' }
];

export function KeycloakFactory(provider: KeycloakService, injector: Injector): Function {
  const loadFactory = () =>
    new Promise<any>((resolve: any) => {
      const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
      locationInitialized.then(() => {
        provider.load().subscribe(() => resolve(null));
      });
    });

  return loadFactory;
}

export function ModuleConfigurationFactory(options: ModuleOptionsModel): ModuleConfigurationService {
  const configurationService: ModuleConfigurationService = new ModuleConfigurationService();
  configurationService.options = options;
  return configurationService;
}

@Injectable()
export class ParameterHashLocationStrategy extends HashLocationStrategy {
  prepareExternalUrl(internal: string): string {
    return window.location.search + super.prepareExternalUrl(internal);
  }
}

@NgModule({
  declarations: [...NliComponents, ColorsGrabberComponent],
  exports: NliComponents,
  providers: [{ provide: LOCALE_ID, useValue: 'de' }],
  imports: [
    CommonModule,
    MaterialModule,
    HttpClientModule,
    TranslateModule.forRoot(),
    RouterModule,
    AuthModule.forRoot(),
    NgxWebstorageModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    NliMenuModule
  ]
})
export class NliCommonModule {
  static forRoot(options?: ModuleOptionsModel): ModuleWithProviders<NliCommonModule> {
    return {
      ngModule: NliCommonModule,
      providers: [
        SetupGuard,
        {
          provide: LocationStrategy,
          useClass: ParameterHashLocationStrategy
        },
        KeycloakService,
        AuthenticationService,
        OidcSecurityService,
        { provide: APP_INITIALIZER, useFactory: KeycloakFactory, deps: [KeycloakService, Injector], multi: true },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: JwtInterceptor,
          deps: [KeycloakService, Injector],
          multi: true
        },
        ClientService,
        TitleConfigService,
        FilterSearchStateService,
        {
          provide: FOR_ROOT_OPTIONS_TOKEN,
          useValue: options
        },
        {
          provide: ModuleConfigurationService,
          useFactory: ModuleConfigurationFactory,
          deps: [FOR_ROOT_OPTIONS_TOKEN]
        }
      ]
    };
  }
}
